import React, { ForwardedRef, MutableRefObject } from "react";
import { TrackingObject } from "@themuse/design-system/lib/types/TrackingTypes";
import SquareButton from "@themuse/design-system/lib/components/Buttons/SquareButton";
import { TextLinkIcon, TextLinkIconPosition } from "@themuse/design-system/lib/components/Text/TextLink";

import styles from "./JobQuizContainer.module.scss";

interface JobQuizNavigationProps {
    galleryRef?: ForwardedRef<HTMLDivElement>;
    numSteps: number;
    currentStep?: number;
    showPrevious?: boolean;
    showNext?: boolean;
    prevText?: string;
    nextText?: string;
    submitText?: string;
    canSkip?: boolean;
    skipText?: string;
    disableNext?: boolean;
    clickTrackingData?: TrackingObject;
    onSkip?: (step: number) => void;
    onStepChange?: (step: number) => void;
    onSubmit?: () => void;
}

/**
 * Renders a navigation button group and progress bar for a job quiz.
 * Handles navigation between steps, skipping steps, and submitting the quiz.
 * Uses props to customize button texts, tracking data, and behavior.
 */
const JobQuizNavigation = (props: JobQuizNavigationProps & React.HTMLProps<HTMLDivElement>) => {
    const {
        galleryRef,
        currentStep,
        disableNext,
        nextText,
        numSteps,
        prevText,
        showPrevious,
        showNext,
        submitText,
        skipText,
        canSkip,
        clickTrackingData,
        onSkip,
        onStepChange,
        onSubmit
    } = props;
    const [step, setStep] = React.useState(currentStep ?? 1);
    const [nextDisabled, setNextDisabled] = React.useState(disableNext ?? false);
    const willShowNext = typeof showNext === "undefined" ? true : !!showNext;
    const skippable = typeof canSkip === "undefined" ? true : !!canSkip;
    const willShowPrevious = showPrevious && step > 1;

    const percent = Math.floor((step / numSteps) * 100);
    const progressStateStyle = {
        width: `${percent}%`
    };
    const nextAvailable = step < numSteps;
    const isLastStep = step === numSteps;
    const displayTextNext = isLastStep ? submitText : nextText;

    // automatically set the current step if it changes (this is useful for autonav and reset)
    React.useEffect(() => {
        if (currentStep) {
            setStep(currentStep);
        }
    }, [currentStep]);

    // When the step changes, we use the galleryRef to 'scroll' to the new question.
    React.useEffect(() => {
        const gallery = (galleryRef as MutableRefObject<HTMLDivElement>)?.current;

        if (gallery) {
            const galleryWidth = gallery.clientWidth || 0;

            gallery.scrollTo({
                left: (step - 1) * (galleryWidth),
                behavior: "smooth"
            });
        }
    }, [galleryRef, step]);

    React.useEffect(() => {
        if (typeof disableNext !== "undefined") {
            setNextDisabled(disableNext);
        }
    }, [disableNext]);

    const handlePrevious = () => {
        if (!willShowPrevious) return;

        const newStep = step - 1;

        setStep(newStep);
        onStepChange?.(newStep);
    };

    const handleSkip = () => {
        if (!skippable) return;
        const newStep = step + 1;

        setStep(newStep);
        onSkip?.(newStep);
    };

    const handleNext = () => {
        if (!nextAvailable) return;
        if (nextDisabled) return;

        const newStep = step + 1;

        setStep(newStep);
        onStepChange?.(newStep);
    };

    const handleSubmit = () => {
        setStep(step + 1);
        onSubmit?.();
    };

    const previousButtonProps = {
        icon: "arrowLeft" as TextLinkIcon,
        position: "left" as TextLinkIconPosition,
        onClick: handlePrevious
    };

    const nextButtonProps = {
        disabled: nextDisabled && !isLastStep,
        onClick: handleNext
    };

    const submitButtonProps = {
        onClick: handleSubmit
    };
    return (
        <div className={styles.navigation}>
            <div className={styles.progressBar}>
                <div className={styles.progressState} style={progressStateStyle} />
            </div>
            <div className={styles.navButtons}>
                {willShowPrevious
                && (
                    <SquareButton
                        className={styles.prevButton}
                        data-tracking-props={JSON.stringify({
                            ...clickTrackingData,
                            sp_click_button_text: prevText
                        })}
                        data-tracking-event-type="click"
                        {...previousButtonProps}
                    >
                        {prevText}
                    </SquareButton>
                )}
                <div className={styles.spacer} />

                <div className={styles.navRight}>
                    {skippable && !isLastStep
                    && (
                        <SquareButton
                            className={styles.prevButton}
                            onClick={handleSkip}
                            data-tracking-props={JSON.stringify({
                                ...clickTrackingData,
                                sp_click_button_text: skipText
                            })}
                            data-tracking-event-type="click"
                        >
                            {skipText}
                        </SquareButton>
                    )}
                    {willShowNext && !isLastStep
                    && (
                        <SquareButton
                            className={styles.nextButton}
                            {...nextButtonProps}
                            data-tracking-props={JSON.stringify({
                                ...clickTrackingData,
                                sp_click_button_text: displayTextNext
                            })}
                            data-tracking-event-type="click"
                        >
                            {displayTextNext}
                        </SquareButton>
                    )}
                    {isLastStep
                    && (
                        <SquareButton
                            className={styles.nextButton}
                            {...submitButtonProps}
                            data-tracking-props={JSON.stringify({
                                ...clickTrackingData,
                                sp_click_button_text: submitText,
                                sp_click_target_page_section: "search"
                            })}
                            data-tracking-event-type="click"
                        >
                            {submitText}
                        </SquareButton>
                    )}
                </div>
            </div>
        </div>
    );
};
JobQuizNavigation.displayName = "JobQuizNavigation";

/**
 * Wraps the JobQuizNavigation so we can pass a Ref to it as a child element..
 */
const JobQuizNavigationRef = React.forwardRef((props: JobQuizNavigationProps, ref: React.Ref<HTMLDivElement>) => (
    <JobQuizNavigation galleryRef={ref} {...props} />
));
JobQuizNavigationRef.displayName = "JobQuizNavigationRef";

export { JobQuizNavigationRef };
export { JobQuizNavigation as default };
export type { JobQuizNavigationProps };
